if (!customElements.get('aurlona-favorites')) {
  customElements.define('aurlona-favorites', class extends HTMLElement {
    connectedCallback() {
      this.events = new AbortController();
      this.warmed = new Set();
      this.images = new Set();
      const signal = this.events.signal;
      this.tabs = [...this.querySelectorAll('[data-favorites-tab]')].map(link => {
        if (link.tagName === 'BUTTON') return link;
        const button = document.createElement('button');
        [...link.attributes].forEach(attribute => {
          if (attribute.name !== 'href') button.setAttribute(attribute.name, attribute.value);
        });
        button.type = 'button';
        button.dataset.collectionUrl = link.getAttribute('href');
        button.textContent = link.textContent;
        link.replaceWith(button);
        return button;
      });
      this.panels = new Map([...this.querySelectorAll('[data-favorites-panel]')].map(panel => [panel.id, panel]));
      this.status = this.querySelector('[data-favorites-status]');
      this.tablist = this.querySelector('[data-favorites-tablist]');
      if (!this.tabs.length) return;
      this.tablist.setAttribute('role', 'tablist');
      this.tabs.forEach(tab => {
        const panel = this.panels.get(tab.dataset.panelId);
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', panel.id);
        tab.setAttribute('aria-selected', String(!panel.hidden));
        tab.removeAttribute('aria-current');
        tab.tabIndex = panel.hidden ? -1 : 0;
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tab.id);
        panel.tabIndex = 0;
      });
      this.active = this.tabs.find(tab => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
      this.addEventListener('click', event => {
        const tab = event.target.closest('[data-favorites-tab]');
        if (!this.tabs.includes(tab)) return;
        event.preventDefault();
        this.select(tab);
      }, {signal});
      this.addEventListener('keydown', event => {
        const index = this.tabs.indexOf(event.target);
        if (index < 0) return;
        const targets = {ArrowRight: (index + 1) % this.tabs.length, ArrowLeft: (index - 1 + this.tabs.length) % this.tabs.length, Home: 0, End: this.tabs.length - 1};
        if (Object.hasOwn(targets, event.key)) {
          event.preventDefault();
          const tab = this.tabs[targets[event.key]];
          tab.focus();
          this.reveal(tab);
        }
      }, {signal});
      ['pointerover', 'focusin'].forEach(type => this.tablist.addEventListener(type, event => {
        const tab = event.target.closest('[data-favorites-tab]');
        if (tab) this.warmImages(this.panels.get(tab.dataset.panelId));
      }, {signal}));
      // Selecting a category in Shopify's sidebar also opens its preview.
      this.addEventListener('shopify:block:select', event => {
        const tab = this.tabs.find(item => item.dataset.blockId === event.detail.blockId);
        if (tab) this.select(tab);
      }, {signal});
      this.observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.observer.disconnect();
        const prepare = () => {
          if (!this.isConnected) return;
          if ('requestIdleCallback' in window) this.idle = requestIdleCallback(() => this.prepareImages(), {timeout: 1200});
          else this.prepareImages();
        };
        if (document.readyState === 'complete') prepare();
        else window.addEventListener('load', prepare, {once: true, signal});
      }, {rootMargin: '250px'});
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.events?.abort();
      this.observer?.disconnect();
      if (this.idle !== undefined && 'cancelIdleCallback' in window) cancelIdleCallback(this.idle);
      cancelAnimationFrame(this.frame);
      this.images?.clear();
    }

    visibleCount() {
      const mobile = !matchMedia('(min-width: 768px)').matches;
      const columns = Math.max(1, Number(matchMedia('(min-width: 1150px)').matches ? this.dataset.columnsDesktop : mobile ? this.dataset.columnsMobile : this.dataset.columnsTablet));
      return columns * (mobile && this.dataset.rowsMobile === '2' ? 2 : 1);
    }

    prepareImages() {
      this.panels.forEach(panel => this.warmImages(panel));
    }

    warmImages(panel) {
      if (!panel) return;
      const content = panel.querySelector('template[data-favorites-content]')?.content || panel;
      [...content.querySelectorAll('hdt-card-product')].slice(0, this.visibleCount()).forEach(card => {
        const image = card.querySelector('.hdt-card-product__media img');
        if (!image) return;
        const src = image.getAttribute('src');
        const srcset = image.getAttribute('srcset') || '';
        const sizes = image.getAttribute('sizes') || '';
        const key = srcset + sizes + src;
        if (!src || this.warmed.has(key)) return;
        this.warmed.add(key);
        const preview = new Image();
        preview.decoding = 'async';
        preview.fetchPriority = 'low';
        this.images.add(preview);
        preview.onload = preview.onerror = () => this.images.delete(preview);
        if (sizes) preview.sizes = sizes;
        if (srcset) preview.srcset = srcset;
        preview.src = src;
      });
    }

    reveal(tab) {
      const rect = tab.getBoundingClientRect();
      const parent = this.tablist.getBoundingClientRect();
      if (rect.left < parent.left) this.tablist.scrollLeft -= parent.left - rect.left + 8;
      if (rect.right > parent.right) this.tablist.scrollLeft += rect.right - parent.right + 8;
    }

    select(tab) {
      const panel = this.panels.get(tab.dataset.panelId);
      if (!panel) return;
      this.panels.forEach(item => { item.hidden = item !== panel; });
      const template = panel.querySelector('template[data-favorites-content]');
      if (template) template.replaceWith(template.content);
      this.tabs.forEach(item => {
        item.setAttribute('aria-selected', String(item === tab));
        item.tabIndex = item === tab ? 0 : -1;
      });
      this.querySelectorAll('[data-favorites-view-all]').forEach(link => { link.href = tab.dataset.collectionUrl; });
      this.active = tab;
      this.status.textContent = `${tab.textContent.trim()} artworks shown.`;
      this.reveal(tab);
      // Eager-load only the cards visible in the current mobile/desktop layout.
      [...panel.querySelectorAll('hdt-card-product')].slice(0, this.visibleCount()).forEach(card => {
        const image = card.querySelector('.hdt-card-product__media img');
        if (image) image.loading = 'eager';
      });
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        const slider = panel.querySelector('hdt-slider');
        if (this.isConnected && !panel.hidden && typeof slider?.reInit === 'function') slider.reInit();
      });
    }
  });
}
